import { useState } from "react";
import { motion } from "framer-motion";
import {
    Phone,
    Mail,
    MapPin,
    Globe,
    Clock,
    Send,
    CheckCircle2,
    Facebook,
    Car,
    Music2,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SITE } from "@/lib/site";

const TILES = [
    {
        icon: MapPin,
        label: "Адрес",
        value: SITE.address,
        href: SITE.googleMaps,
        external: true,
    },
    {
        icon: Phone,
        label: "Телефон",
        value: SITE.phone,
        href: `tel:${SITE.phoneRaw}`,
        external: false,
    },
    {
        icon: Mail,
        label: "Имейл",
        value: SITE.email,
        href: `mailto:${SITE.email}`,
        external: false,
    },
    {
        icon: Globe,
        label: "Уебсайт",
        value: "www.konstantin-mobile.bg",
        href: "https://www.konstantin-mobile.bg/",
        external: true,
    },
];

function ContactTile({ icon: Icon, label, value, href, external }) {
    return (
        <a
            href={href}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
            className="group flex flex-col items-center text-center bg-surface/80 backdrop-blur-sm border border-line hover:border-gold/40 rounded-3xl px-6 py-10 transition-all shadow-soft hover:-translate-y-1"
            data-testid={`contact-tile-${label.toLowerCase()}`}
        >
            <div className="w-[88px] h-[88px] rounded-full bg-bg shadow-soft flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <Icon size={36} className="text-ink/90 group-hover:text-gold transition-colors" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-ink-subtle mb-2">
                {label}
            </div>
            <div className="text-base text-ink/90 break-all">{value}</div>
        </a>
    );
}

export default function Contact() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });
    const [status, setStatus] = useState({ loading: false, ok: false, error: "" });

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        setStatus({ loading: true, ok: false, error: "" });
        try {
            await api.post("/contact", form);
            setStatus({ loading: false, ok: true, error: "" });
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch (err) {
            setStatus({
                loading: false,
                ok: false,
                error: formatApiError(err?.response?.data?.detail) || err.message,
            });
        }
    };

    return (
        <section
            id="contact"
            className="relative py-28 lg:py-36 overflow-hidden"
            data-testid="contact-section"
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                {/* Header — matches konstantin-mobile.bg simple style */}
                <div className="text-center mb-16">
                    <h2
                        className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tightest mb-4"
                        data-testid="contact-heading"
                    >
                        Контакт
                    </h2>
                    <p className="text-[11px] uppercase tracking-[0.45em] text-gold">
                        KONSTANTIN MOBILE
                    </p>
                </div>

                {/* 4 minimal contact tiles — replicates konstantin-mobile.bg layout */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16"
                >
                    {TILES.map((tile) => (
                        <ContactTile key={tile.label} {...tile} />
                    ))}
                </motion.div>

                {/* Hours + Socials row */}
                <div className="grid md:grid-cols-2 gap-6 mb-16">
                    <div className="bg-surface/80 backdrop-blur-sm border border-line rounded-3xl p-8 flex items-start gap-5">
                        <div className="w-12 h-12 rounded-full bg-gold/10 border border-gold/20 text-gold flex items-center justify-center shrink-0">
                            <Clock size={20} />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.32em] text-ink-subtle mb-2">
                                Работно време
                            </div>
                            <div className="text-ink/90">{SITE.hoursWeekdays}</div>
                            <div className="text-ink-muted">{SITE.hoursWeekend}</div>
                        </div>
                    </div>

                    <div className="bg-surface/80 backdrop-blur-sm border border-line rounded-3xl p-8 flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.32em] text-ink-subtle mb-2">
                                Социални
                            </div>
                            <div className="text-ink/90">Последвай ни</div>
                        </div>
                        <div className="flex items-center gap-3">
                            <a
                                href={SITE.facebook}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="Facebook"
                                className="w-11 h-11 rounded-full border border-line hover:border-gold/60 hover:text-gold flex items-center justify-center transition-all"
                                data-testid="social-facebook"
                            >
                                <Facebook size={18} />
                            </a>
                            <a
                                href={SITE.mobileBg}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="mobile.bg"
                                title="Виж обявите ни в mobile.bg"
                                className="w-11 h-11 rounded-full border border-line hover:border-gold/60 hover:text-gold flex items-center justify-center transition-all"
                                data-testid="social-mobilebg"
                            >
                                <Car size={18} />
                            </a>
                            <a
                                href={SITE.tiktok}
                                target="_blank"
                                rel="noreferrer"
                                aria-label="TikTok"
                                className="w-11 h-11 rounded-full border border-line hover:border-gold/60 hover:text-gold flex items-center justify-center transition-all"
                                data-testid="social-tiktok"
                            >
                                <Music2 size={18} />
                            </a>
                        </div>
                    </div>
                </div>

                {/* Map */}
                <div
                    className="rounded-3xl overflow-hidden border border-line shadow-soft mb-16"
                    data-testid="contact-map"
                >
                    <iframe
                        title="map"
                        src={SITE.mapsEmbed}
                        width="100%"
                        height="480"
                        loading="lazy"
                        style={{ border: 0 }}
                        allowFullScreen
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>

                {/* Optional message form */}
                <motion.form
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7 }}
                    className="bg-surface/80 backdrop-blur-sm rounded-3xl p-8 lg:p-10 border border-line"
                    data-testid="contact-form"
                >
                    <div className="text-center mb-8">
                        <h3 className="font-display text-3xl mb-2">Изпратете ни съобщение</h3>
                        <p className="text-ink-muted text-sm">
                            Готови сме да ви помогнем при избор, оглед или индивидуални условия.
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5 max-w-3xl mx-auto">
                        <Field label="Име" value={form.name} onChange={(v) => set("name", v)} testid="contact-name" required />
                        <Field label="Имейл" type="email" value={form.email} onChange={(v) => set("email", v)} testid="contact-email-input" required />
                        <Field label="Телефон" value={form.phone} onChange={(v) => set("phone", v)} testid="contact-phone-input" />
                        <Field label="Тема" value={form.subject} onChange={(v) => set("subject", v)} testid="contact-subject" />
                    </div>
                    <div className="mt-5 max-w-3xl mx-auto">
                        <label className="block bg-bg border border-line rounded-xl px-5 py-4 hover:border-gold/40 transition-all">
                            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                                Съобщение *
                            </span>
                            <textarea
                                required
                                rows={5}
                                value={form.message}
                                onChange={(e) => set("message", e.target.value)}
                                className="w-full mt-2 bg-transparent text-sm focus:outline-none resize-none placeholder:text-ink-subtle"
                                placeholder="Разкажете ни как можем да ви съдействаме…"
                                data-testid="contact-message"
                            />
                        </label>
                    </div>

                    {status.error && (
                        <div className="mt-5 text-sm text-red-600 text-center" data-testid="contact-error">
                            {status.error}
                        </div>
                    )}
                    {status.ok && (
                        <div
                            className="mt-5 flex items-center justify-center gap-2 text-sm text-gold"
                            data-testid="contact-success"
                        >
                            <CheckCircle2 size={16} /> Благодарим ви — съобщението е изпратено успешно.
                        </div>
                    )}

                    <div className="mt-7 flex justify-center">
                        <button
                            type="submit"
                            disabled={status.loading}
                            className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-champagne font-medium px-10 py-4 rounded-full transition-all disabled:opacity-60 shadow-gold"
                            data-testid="contact-submit"
                        >
                            {status.loading ? "Изпращане…" : (
                                <>
                                    Изпрати съобщение <Send size={16} />
                                </>
                            )}
                        </button>
                    </div>
                </motion.form>
            </div>
        </section>
    );
}

function Field({ label, value, onChange, type = "text", testid, required }) {
    return (
        <label className="block bg-bg border border-line rounded-xl px-5 py-4 hover:border-gold/40 transition-all">
            <span className="block text-[10px] uppercase tracking-[0.25em] text-ink-subtle">
                {label} {required && "*"}
            </span>
            <input
                type={type}
                required={required}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full mt-1 bg-transparent text-sm focus:outline-none placeholder:text-ink-subtle"
                data-testid={testid}
            />
        </label>
    );
}
